import { useQuery } from '@apollo/client';
import { NoResults as NoResultsIcon } from '@clearkit/icons';
import { CKSearch } from 'clearkit';
import { Link } from 'react-router-dom';

import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import Tile from '~/components/SegmentTile';
import TimeAgo from '~/components/TimeAgo';
import paths from '~/lib/paths';

import query from './query.graphql';

const PersonSegments = ({ person: { id } }) => {
  const [search, setSearch] = useState('');

  const { data, error, loading, refetch } = useQuery(query, {
    variables: { id },
    returnPartialData: true,
  });

  if (!data && loading) return <LoadingArea />;

  const {
    person: { segments },
  } = data;

  let filteredSegments = segments;
  if (search) {
    filteredSegments = segments.filter((s) =>
      s.name.toLowerCase().includes(search.toLowerCase()),
    );
  }

  return (
    <div className="flex flex-col flex-grow flex-shrink min-h-0 bg-gray-0">
      <GraphqlError className="mb-4" error={error} refetch={refetch} />
      <div className="flex flex-col flex-shrink min-h-0 px-4 py-6">
        <div className="px-4">
          {segments?.length === 0 ? (
            <div className="flex flex-col items-center pt-8 mx-auto text-center w-xs">
              <NoResultsIcon className="text-gray-200" height={40} width={40} />
              <p className="mt-3 mb-1 text-base text-gray-600">
                This person doesn't appear in any of your audiences.
              </p>
            </div>
          ) : (
            <CKSearch
              className="mb-5"
              onChange={setSearch}
              placeholder="Search for an audience"
              value={search}
            />
          )}
        </div>

        <div className="flex flex-col flex-shrink min-h-0 px-4 py-1 overflow-auto">
          {filteredSegments?.map((segment) => {
            const destinationsAndAudiences = [
              ...segment.destinations,
              ...segment.audiences.edges,
            ];

            return (
              <Link
                className="flex flex-row flex-shrink-0 p-4 mb-4 text-gray-700 bg-white rounded-md ck-box"
                key={segment.id}
                to={paths.segment(segment.type, segment.id)}
              >
                <Tile className="mr-4" type={segment.type} />

                <div className="flex flex-col">
                  <span className="font-semibold">{segment.name}</span>
                  <span>{segment.collection.name}</span>
                </div>

                <div className="flex flex-col ml-auto text-sm leading-relaxed text-right">
                  <span>
                    {destinationsAndAudiences.length > 0 && (
                      <>{destinationsAndAudiences.length} destinations</>
                    )}
                  </span>
                  <span>
                    Updated <TimeAgo time={segment.updatedAt} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

PersonSegments.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default PersonSegments;
